import React from "react";
import {
  Pill,
  CheckCircle2,
  ShieldAlert,
  FileSpreadsheet,
  Lock,
  CalendarRange,
  Workflow,
  UserCheck,
} from "lucide-react";
import HeaderMenu from "../../component/Navbar/headermenu";
import CustomFooter from "../../component/Footer/customFooter";
import ShowcaseImageLightbox from "../../component/ShowcaseImageLightbox";
import "./about.css";
import taskList from "../../assets/new-images/pharma1.png";
import taskDetail from "../../assets/new-images/pharma2.png";
import manageRequests from "../../assets/new-images/it4.png";
import manageTimesheets from "../../assets/new-images/it5.png";

const features = [
  {
    icon: <ShieldAlert size={26} />,
    title: "GMP & Batch Compliance",
    description:
      "Ensure all quality controls and production checksheets conform to Good Manufacturing Practices.",
  },
  {
    icon: <FileSpreadsheet size={26} />,
    title: "Batch Record Tracking",
    description:
      "Track every batch release from initial QA audits to final certification in a central record.",
  },
  {
    icon: <Lock size={26} />,
    title: "Audit Trails & Security",
    description:
      "Keep a detailed, electronic timestamp log of who completed and approved every process step.",
  },
  {
    icon: <CalendarRange size={26} />,
    title: "Regulatory Timeline",
    description:
      "Track DMF updates, license renewals, and import/export regulatory filing deadlines.",
  },
];

const PharmaCompaniesPage = () => {
  const showcase = [
    {
      eyebrow: "Regulatory & Batch Tracking",
      title: "Every regulatory task, from inventory audit to batch release",
      description:
        "Inventory audits, license renewals, pharmacovigilance, export documentation, clinical trials, supply chain, GMP compliance audits, product launches, batch releases, and regulatory filings — all tracked as tasks with client, status, period, and financial year in one board.",
      image: taskList,
      alt: "Simplifyte task list showing pharma tasks including Inventory Audit, License Renewal, Pharmacovigilance, Export Documentation, Clinical Trial, Supply Chain, Compliance Audit, Product Launch, Batch Release, and Regulatory Filing with client and status columns",
    },
    {
      eyebrow: "Task-level detail",
      title: "Every task, with the client and deadline context that matters",
      description:
        "Open any task to see its client, financial year, period, due date, and assignee at a glance — so a task like 'QA sign-off for Batch #A2607' under Batch Release carries exactly the context QA and regulatory teams need, without a separate lookup.",
      image: taskDetail,
      alt: "Simplifyte task detail panel showing a QA sign-off for Batch task under Batch Release - Amrit Pharma, with client, financial year, due date, and assignee",
    },
    {
      eyebrow: "Batch Record Tracking",
      title: "Accurate batch records across every production cycle",
      description:
        "Track batch records and quality checks the same way you track any task — assigned, dated, and status-tagged — so every production cycle has a clear, auditable trail from start to sign-off.",
      image: taskList,
      alt: "Simplifyte task list used to track batch records and production status across clients",
    },
    {
      eyebrow: "Compliance & Audit Trail",
      title: "Inspection-ready, every day of the year",
      description:
        "Every document, approval, and status change carries a full history of who touched it and when — so you're always ready for an audit instead of scrambling to reconstruct one after the fact.",
      image: taskDetail,
      alt: "Simplifyte task detail panel showing financial year, due date, and assignee history for an audit-ready task",
    },
    {
      eyebrow: "Team & Workforce Management",
      title: "Leave, attendance, and presence requests, approved in one place",
      description:
        "Leave requests, present requests, and approvals for lab, QA, and regulatory staff flow through one queue with requester, remarks, and status — so plant managers aren't chasing sign-offs over email during a batch release.",
      image: manageRequests,
      alt: "Simplifyte Manage Requests showing a table of leave and present requests with requester, remarks, status, and action-by columns",
    },
    {
      eyebrow: "Attendance & Timesheets",
      title: "Check-ins, overtime, and absences, at a glance",
      description:
        "See present records, late arrivals, overtime, and absences for the entire production and QA team in one timesheet view — so shift coverage gaps surface immediately instead of at month-end.",
      image: manageTimesheets,
      alt: "Simplifyte Manage Timesheets showing team check-in, check-out, work time, overtime, and break time for each employee with status labels",
    },
  ];

  const benefits = [
    "Reduce regulatory documentation errors by half",
    "Track every approval and submission deadline in one place",
    "Maintain inspection-ready audit trails automatically",
    "Standardize batch record workflows across facilities",
    "Cut time spent chasing sign-offs across departments",
  ];

  return (
    <>
      <HeaderMenu />
      <div className="Pharma_class_main">
        {/* Hero */}
        <section className="Pharma_class_hero">
          <div className="Pharma_class_container">
            <div className="Pharma_class_hero_content">
              <div className="Pharma_class_icon_box">
                <Pill size={34} />
              </div>

              <h1 className="Pharma_class_heading">
                Compliance workflow software for Pharma companies
              </h1>

              <p className="Pharma_class_subheading">
                Stay on top of regulatory documentation, batch records, and
                approval timelines without chasing scattered files.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="Pharma_class_features_section">
          <div className="Pharma_class_container">
            <h2 className="Pharma_class_section_title Pharma_class_center">
              Everything pharma teams need in one platform
            </h2>

            <div className="Pharma_class_features_grid">
              {features.map((feature, index) => (
                <div key={index} className="Pharma_class_feature_card">
                  <div className="Pharma_class_feature_icon">
                    {feature.icon}
                  </div>

                  <h3 className="Pharma_class_feature_title">
                    {feature.title}
                  </h3>

                  <p className="Pharma_class_feature_description">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Showcase */}
        <section className="Pharma_class_showcase_section">
          <div className="Pharma_class_container">
            <h2 className="Pharma_class_section_title Pharma_class_center">
              Everything you need for regulated production
            </h2>
            <p className="Pharma_class_showcase_subtitle">
              See how pharma teams run documentation, batch records, and
              approvals on Simplifyte.
            </p>

            <div className="Pharma_class_showcase_list">
              {showcase.map((item, index) => (
                <div
                  key={index}
                  className={`Pharma_class_showcase_row ${
                    index % 2 === 1 ? "Pharma_class_showcase_row_reverse" : ""
                  }`}
                >
                  <div className="Pharma_class_showcase_text">
                    <span className="Pharma_class_showcase_eyebrow">
                      {item.eyebrow}
                    </span>
                    <h3 className="Pharma_class_showcase_title">
                      {item.title}
                    </h3>
                    <p className="Pharma_class_showcase_description">
                      {item.description}
                    </p>
                  </div>

                  <div className="Pharma_class_showcase_media">
                    <div className="Pharma_class_showcase_frame_outer">
                      <div className="Pharma_class_showcase_frame_inner">
                        <ShowcaseImageLightbox
                          src={item.image}
                          alt={item.alt}
                          className="Pharma_class_showcase_image"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="Pharma_class_benefits_section">
          <div className="Pharma_class_container">
            <div className="Pharma_class_benefits_wrapper">
              <h2 className="Pharma_class_section_title Pharma_class_center">
                Why pharma companies choose Simplifyte
              </h2>

              <div className="Pharma_class_benefits_list">
                {benefits.map((benefit, index) => (
                  <div key={index} className="Pharma_class_benefit_item">
                    <CheckCircle2
                      size={24}
                      className="Pharma_class_check_icon"
                    />

                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="Pharma_class_cta_section">
          <div className="Pharma_class_container">
            <div className="Pharma_class_cta_box">
              <h2 className="Pharma_class_cta_title">
                Ready to simplify your compliance workflow?
              </h2>

              <p className="Pharma_class_cta_text">
                Join pharma teams who've brought clarity to regulatory
                documentation and approvals.
              </p>

              <button
                className="Pharma_class_cta_button"
                onClick={() =>
                  (window.location.href = "https://app.simplifyte.com")
                }
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </section>
      </div>
      <CustomFooter />
    </>
  );
};

export default PharmaCompaniesPage;
